/**
 * Utility to capture UTM parameters from URL
 * 
 * Uses BOTH localStorage AND sessionStorage for maximum durability.
 * On the very first page load that carries UTM query-string params, they are
 * persisted and re-used for every submission until the next visit with fresh
 * UTM params. document.referrer is also captured on first load.
 *
 * Belt-and-suspenders: stores in two keys so that privacy tools or ITP clearing
 * one storage mechanism won't lose attribution data.
 */

import debug from './debug';

const STORAGE_KEY = 'utm_parameters';
const BACKUP_STORAGE_KEY = 'lh_utm'; // shorter key stored in sessionStorage as backup

const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'source',
  'ref',
  'referrer'
];

/**
 * Extract UTM parameters from the current URL query string.
 * Also captures document.referrer as a fallback referrer value.
 */
export const getUtmParameters = () => {
  try {
    const search = window.location.search;
    const hash = window.location.hash;

    // Some redirectors put query params after the hash — try both
    const urlParams = new URLSearchParams(search);
    let hashParams = null;
    if (hash && hash.includes('?')) {
      hashParams = new URLSearchParams(hash.split('?')[1]);
    }

    const utmData = {};

    UTM_PARAMS.forEach(param => {
      const value = urlParams.get(param) || (hashParams && hashParams.get(param));
      if (value) {
        utmData[param] = decodeURIComponent(value);
      }
    });

    // Capture the full query string for diagnostics if UTM params are present
    if (Object.keys(utmData).length > 0) {
      utmData._capturedAt = new Date().toISOString();
      utmData._landingPage = window.location.pathname;
    }

    if (!utmData.referrer && !utmData.ref && document.referrer) {
      try {
        const ref = new URL(document.referrer);
        if (ref.hostname !== window.location.hostname) {
          utmData.referrer = document.referrer;
        }
      } catch {
        utmData.referrer = document.referrer;
      }
    }

    debug.log('UTM', 'Extracted UTM parameters', utmData);
    return utmData;
  } catch (error) {
    debug.error('UTM', 'Error extracting UTM parameters', error);
    return {};
  }
};

/**
 * Persist UTM parameters to BOTH localStorage and sessionStorage.
 * Only overwrites existing data when new UTM params are present in the URL —
 * internal navigations (empty query strings) will never clear stored values.
 *
 * Merges with existing stored data so referrer captured on page 1 isn't lost
 * when UTM params are captured on a subsequent navigation.
 */
export const storeUtmParameters = (utmParams) => {
  try {
    const freshParams = utmParams || getUtmParameters();

    if (Object.keys(freshParams).length === 0) return;

    // Merge with any previously stored params (first-touch values preserved,
    // new params added or overwritten with last-touch)
    const existing = getStoredUtmParameters();
    const merged = { ...existing, ...freshParams };

    const serialized = JSON.stringify(merged);

    try { localStorage.setItem(STORAGE_KEY, serialized); } catch { /* blocked */ }
    try { sessionStorage.setItem(BACKUP_STORAGE_KEY, serialized); } catch { /* blocked */ }

    debug.log('UTM', 'Stored UTM parameters', merged);
  } catch (error) {
    debug.error('UTM', 'Error storing UTM parameters', error);
  }
};

/**
 * Retrieve stored UTM parameters. Checks localStorage first, then
 * sessionStorage backup, then legacy sessionStorage key.
 */
export const getStoredUtmParameters = () => {
  try {
    // Primary: localStorage
    const stored = safeGetItem(localStorage, STORAGE_KEY);
    if (stored) return JSON.parse(stored);

    // Backup: sessionStorage (short key)
    const backup = safeGetItem(sessionStorage, BACKUP_STORAGE_KEY);
    if (backup) {
      // Promote back to localStorage
      try { localStorage.setItem(STORAGE_KEY, backup); } catch { /* blocked */ }
      return JSON.parse(backup);
    }

    // Legacy: old sessionStorage key
    const legacy = safeGetItem(sessionStorage, STORAGE_KEY);
    if (legacy) {
      try { localStorage.setItem(STORAGE_KEY, legacy); } catch { /* blocked */ }
      try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* blocked */ }
      return JSON.parse(legacy);
    }

    return {};
  } catch (error) {
    debug.error('UTM', 'Error retrieving UTM parameters', error);
    return {};
  }
};

function safeGetItem(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Clear stored UTM data (call after successful submission if desired).
 */
export const clearUtmParameters = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(BACKUP_STORAGE_KEY);
  } catch (error) {
    debug.error('UTM', 'Error clearing UTM parameters', error);
  }
};

const utmUtils = {
  getUtmParameters,
  storeUtmParameters,
  getStoredUtmParameters,
  clearUtmParameters
};

export default utmUtils; 